
import { getOrderFromURL } from "../../../utils/order_utils";
import { updateQueries } from "../../../../repository/utils/routing_utils";
import { removeQuery, updateQuery } from "../../../utils/routing_utils";
import { useRoute, useRouter } from "vue-router";
import { dispatchError } from "../../../utils/error_handling";
import { getMenuItems } from "./menu/MenuItemsLoader";
import {
  formatSearchQuery,
  formatUserDecoins,
  isDecolaTemplate
} from "../../mixins";
import { useStore } from "vuex";
import { computed } from "@vue/reactivity";
import {
  defineComponent,
  onBeforeMount,
  ref,
  watch,
  Ref,
  ComputedRef
} from "vue";
import { User } from "../../../../../domain/entities/entities";
import { MenuItem } from "./menu/MenuItem";
import { Order } from "../../../../../domain/entities/order";
import MenuUser from "./menu-user/Menu.vue";
import Notification from "../notification/Notification.vue";
import dashboard from "../../../views/Dashboard.vue";

export default defineComponent({
  methods: { isDecolaTemplate },
  computed: {
    dashboard() {
      return dashboard;
    }
  },
  components: { Notification, MenuUser },
  inheritAttrs: false,
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const inputValue: Ref<string> = ref("");

    /** Key that references the timer in progress. Used to cancel the timeout. */
    let timerId;

    /** Tells whether the dropdown options list is open or not. */
    const headerDropdownIsOpen: ComputedRef<boolean> = computed(() => {
      return store.getters["miscellany_module/headerDropdownIsOpen"];
    });
    /** Tells whether the sidebar is open or not. */
    const sidebarIsOpen: ComputedRef<boolean> = computed(() => {
      return store.getters["miscellany_module/sidebarIsOpen"];
    });

    /** Tells whether the user is logged in or not. */
    const userIsLogged: ComputedRef<boolean> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    /** Photo of the user in the following format "url(http://example.png)". */
    const userPhoto: ComputedRef<string> = computed(() => {
      const user: User = store.getters["userProfile_module/user"];
      return user.image;
    });

    /** User getter.
     * @see User. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    const configuration = computed(
      () => store.getters["configuration_module/configuration"]
    );

    const filteredHeaderNavItems = computed(() => {
      if (!configuration.value.isMaintenanceMode) {
        return headerNavItems.value;
      }
      return headerNavItems.value.filter(
        item => item.title === "Painel Admin."
      );
    });

    const searchQuery: Ref<string> = ref("");

    /** List of links that displays at the nav bar. */
    const headerNavItems: Ref<MenuItem[]> = ref([]);

    watch(
      () => route.query.search,
      () => {
        if (
          route.name === "details_course_tracks" ||
          route.name === "details_courses" ||
          route.name === "global_search" ||
          route.name === "details_courses_instructors"
        ) {
          if (!route.query.search) {
            removeQuery(router, "search");
            router.push({ name: "dashboard" });
          } else {
            searchQuery.value = String(route.query.search);
          }
        } else {
          searchQuery.value = "";
        }
      }
    );

    onBeforeMount(async () => {
      headerNavItems.value = getMenuItems();
      const order = getOrderFromURL(route);
      try {
        await store.dispatch(
          "shop_module/setDecoinsInWallet",
          user.value.decoins
        );

        if (route.query.search) {
          searchQuery.value = String(route.query.search);
          await loadGlobalSchools(String(route.query.search));
          await loadGlobalCourseTracks(String(route.query.search));
          await loadGlobalInstructors(String(route.query.search));
          await loadGlobalCourses(order);
        } else {
          await removeQuery(router, "search");
        }
      } catch (e) {
        await dispatchError(e);
      }
    });

    /** Change dropdown options list visibility priority if open or closed. */
    function toggleHeaderOptionsState(): void {
      store.dispatch("miscellany_module/toggleHeaderOptionsState");
    }

    /** Function used to search for products by name. */
    async function globalSearch(event: Event): Promise<void> {
      const input = event.target as HTMLInputElement;
      searchQuery.value = input.value;

      clearTimeout(timerId);

      timerId = setTimeout(async () => {
        await router.push("/search");
        await updateQueries(router, {
          page: "1",
          limit: "20",
          search: searchQuery.value
        });
        const order = getOrderFromURL(route);
        await loadGlobalSchools(formatSearchQuery(String(route.query.search)));
        await loadGlobalCourseTracks(
          formatSearchQuery(String(route.query.search))
        );
        await loadGlobalInstructors(
          formatSearchQuery(String(route.query.search))
        );
        await loadGlobalCourses(order);
      }, 500);
    }

    const loadLogo = (): string => {
      if (process.env.VUE_APP_THEME === "circulo") {
        return require("../../../assets/static/companies_logos/ganhemais-white.svg");
      } else {
        return require("../../../assets/static/decola_logos/new_logo_sem_slogan_white.svg");
      }
    };

    const loadGlobalCourses = async (order: Order): Promise<void> => {
      try {
        await store.dispatch("globalSearch_module/loadCourses", order);
      } catch (e) {
        console.error(e);
      }
    };

    const loadGlobalSchools = async (search: string): Promise<void> => {
      try {
        await store.dispatch("globalSearch_module/loadSchools", search);
      } catch (e) {
        console.error(e);
      }
    };

    const loadGlobalCourseTracks = async (search: string): Promise<void> => {
      try {
        await store.dispatch("globalSearch_module/loadCourseTracks", search);
      } catch (e) {
        console.error(e);
      }
    };

    const loadGlobalInstructors = async (search: string): Promise<void> => {
      try {
        await store.dispatch("globalSearch_module/loadInstructors", search);
      } catch (e) {
        console.error(e);
      }
    };

    const shouldShowSearchInput = computed(() => {
      const excludedRoutes = [
        "course",
        "course_content",
        "event_content",
        "profile",
        "edit",
        "event",
        "exercise",
        "event_questionnaire",
        "course_questionnaire",
        "user_responses",
        "training-course",
        "statement",
        "shop",
        "product_detail",
        "favorite",
        "bag",
        "interest"
      ];
      return !excludedRoutes.includes(route.name as string);
    });

    const hasCompany = computed(() => {
      return (
        Array.isArray(user.value.companies) &&
        user.value.companies.some(company => company.id > 0)
      );
    });

    return {
      user,
      sidebarIsOpen,
      userIsLogged,
      headerNavItems,
      userPhoto,
      headerDropdownIsOpen,
      inputValue,
      searchQuery,
      shouldShowSearchInput,
      configuration,
      filteredHeaderNavItems,
      hasCompany,
      toggleHeaderOptionsState,
      formatUserDecoins,
      globalSearch,
      updateQuery,
      loadLogo
    };
  }
});
